import React, { useState, useEffect, useContext } from "react";

import { CoreContext } from "../../../context/global/CoreContext";
import { TimekeepingContext } from "../../../context/domain/timekeeping/TimekeepingContext";

import { clockInUser } from "../../../services/api/timekeeping";
import { getJobNumbers } from "../../../services/api/core";

import {
  formatDate,
  findClockedInShifts,
  getCurrentTimeInWindow,
  addHoursTo24TimeString,
} from "../../../utils/datetime";

// Import icons
import { Clock } from "../../../components/icons/svg/outline/index";
import { Button } from "../../../components/ui";
import StatusMessage from "../../../components/common/StatusMessage";

/**
 * ClockIn Component
 *
 * Provides functionality for users to clock in for their work shifts.
 *
 * Features:
 * - Loads user's scheduled job and work scope for the current date
 * - Allows selection of job, work scope, and adding work description
 * - Sets default time to current time, but allows manual adjustment
 * - Handles form validation and submission to clock in API
 * - Displays status messages for user feedback
 * - Animates transition after successful clock in
 *
 * Context Usage:
 * - CoreContext: Retrieves user info and available jobs list
 * - TimekeepingContext: Manages clock in/out state, shift data, and loading states
 *
 * @returns {JSX.Element} The ClockIn component UI
 */
const ClockIn = () => {
  const { user, scopesList } = useContext(CoreContext);
  const {
    date,
    setDate,
    setShowClockOutContent,
    isLoading,
    setIsLoading,
    weeklyShiftData,
    fetchWeeklyShifts,
    dailyShiftData,
    // Use message state from context
    message,
    setMessageDebounced,
  } = useContext(TimekeepingContext); // State management
  const [shiftData, setShiftData] = useState({
    job: "",
    workScope: "",
    workDescription: "",
    startTime: "",
  });
  const [isAnimating, setIsAnimating] = useState(false);
  // Fetch jobs for the specific date (not just today)
  const [jobsList, setJobsList] = useState([]);
  
  // Fetch jobs for the current date whenever date changes
  useEffect(() => {
    const fetchJobsForDate = async () => {
      try {
        const jobs = await getJobNumbers(true, date);
        setJobsList(jobs);
      } catch (error) {
        console.error('Error fetching jobs for date:', error);
        setJobsList([]);
      }
    };
    fetchJobsForDate();
  }, [date]);
  // Check the weekly shift data to see if there are any open clock in events
  useEffect(() => {
    if (weeklyShiftData) {
      const clockedInShifts = findClockedInShifts(weeklyShiftData);

      if (clockedInShifts.length > 0) {
        // Use the first clocked-in shift found
        const firstClockedIn = clockedInShifts[0];

        // Update shift data with the found shift
        setShiftData(firstClockedIn.shift);
        setDate(firstClockedIn.date);

        // Notify user
        setMessageDebounced({
          type: "info",
          text: `You are already clocked in for ${formatDate(
            firstClockedIn.date,
            "month DD, YYYY"
          )}. Please clock out before starting a new shift today.`,
        });
      }
    }
  }, [weeklyShiftData, date, setMessageDebounced, setDate]);

  // Handle field changes
  const handleFieldChange = (field, value) => {
    setShiftData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Format today's date for user display
  const formattedDate = formatDate(date, "day month DD, YYYY");
  // Set default time to current time from client device
  useEffect(() => {
    const now = new Date(); // Creates a date object with the client's local time
    // Format time in 24-hour format (HH:MM)
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    handleFieldChange("startTime", formattedTime);
  }, [setIsLoading]);

  // Get scheduled shift from context shift data, if it exists, to pre-fill form
  useEffect(() => {
    setIsLoading(true);
    setMessageDebounced({
      type: "info",
      text: "Looking for work schedule for " + formattedDate,
    });
    if (dailyShiftData && dailyShiftData.length > 0) {
      const currentShift = dailyShiftData.find(
        (shift) =>
          shift.stage === "Current Shift" || shift.stage === "Missed Shift"
      );
      if (currentShift) {
        // Calculate the appropriate start time based on current time window
        const calculatedStartTime = getCurrentTimeInWindow(
          currentShift.startTime,
          addHoursTo24TimeString(currentShift.startTime, 2),
          currentShift.startTime
        );
        // Update shift data with calculated start time to match what will be displayed
        setShiftData({
          ...currentShift,
          startTime: calculatedStartTime,
        });
        setMessageDebounced({
          type: "success",
          text: "Work schedule loaded for " + formattedDate,
        });
      } else {
        setMessageDebounced({
          type: "warning",
          text:
            `${dailyShiftData.length > 1 ? "Additional scheduled shifts not found for " : "Work schedule not found for "}` +
            formattedDate +
            ". Please enter manually.",
        });
        setIsLoading(false);
      }
    }
  }, [dailyShiftData, formattedDate, setIsLoading, setMessageDebounced]);

  const handleClockIn = async () => {
    setIsLoading(true);
    if (
      !shiftData.job ||
      !shiftData.workScope ||
      !shiftData.startTime ||
      shiftData.job === "" ||
      shiftData.workScope === ""
    ) {
      setMessageDebounced({ type: "error", text: "Please fill in all fields" });
      setIsLoading(false);
      return;
    }

    // Validate start time against daily shift data (cannot clock in before last shift's finish time)
    if (
      dailyShiftData &&
      dailyShiftData.length > 0 &&
      shiftData.startTime <
        dailyShiftData[dailyShiftData.length - 1]?.finishTime
    ) {
      setMessageDebounced({
        type: "error",
        text: "Time in cannot be before previous shift's finish time",
      });
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const result = await clockInUser({
        job: shiftData.job,
        date,
        workScope: shiftData.workScope,
        workDescription: shiftData.workDescription,
        startTime: shiftData.startTime,
        userId: user?.id,
      });
      if (result.success) {
        setMessageDebounced({ type: "success", text: result.message });
        setTimeout(() => {
          setMessageDebounced(null); // Clear message after 2 seconds
        }, 1000);
        // Start animation
        setIsAnimating(true); // Update context to show user is clocked in after animation
        setTimeout(() => {
          fetchWeeklyShifts(); // Fetch weekly shifts to update state
          setTimeout(() => {
            // Allow time for clock out components to load before triggering animations
            setShowClockOutContent(true);
          }, 50);
        }, 1000);
      } else {
        setMessageDebounced({
          type: "error",
          text: result.message || "Clock in failed",
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error clocking in:", error);
      setMessageDebounced({
        type: "error",
        text: "An error occurred while clocking in: " + error.message,
      });
      setIsLoading(false);
    }
  };

  // Allow all messages to display for 0.5 seconds before allowing interaction.
  // This is purely for UX purposes to ensure users see the message before they can interact with the form again.
  setTimeout(() => {
    setIsLoading(false);
  }, 500);

  return (
    <div className="CLOCK-IN">
      {/* Clock In Form */}
      <div
        className={`
        ${isAnimating ? "animate-shrink-up bg-yellow-50" : "bg-gray-100"} 
        p-2 rounded-md
        transition-all duration-1000 ease-in`}
      >
        <div
          className={`${isAnimating ? "opacity-0" : "opacity-100"} transition-opacity ease-in duration-1000`}
        >
          <h2 className="flex items-center text-lg text-black font-medium mb-4">
            <Clock className="text-gray-700 mr-2" />
            Clock In {shiftData.stage ? `(${shiftData.stage})` : "(New Shift)"}
          </h2>{" "}
          {message && <StatusMessage message={message} className="mb-4" />}
          {/* Job Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job
            </label>
            <div className="relative">
              {" "}
              <select
                value={shiftData.job}
                onChange={(e) => handleFieldChange("job", e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              >
                <option value="">Select a Job</option>
                {jobsList.length > 0 ? (
                  jobsList.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.number} - {job.description}
                    </option>
                  ))
                ) : (
                  <option value="">No jobs available</option>
                )}
              </select>
            </div>
          </div>
          {/* Work Scope */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Work Scope
            </label>
            <div className="relative">
              {" "}
              <select
                value={shiftData.workScope}
                onChange={(e) => handleFieldChange("workScope", e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a Scope</option>
                {scopesList.length > 0 ? (
                  scopesList.map((scope) => (
                    <option key={scope.id} value={scope.id}>
                      {scope.title}
                    </option>
                  ))
                ) : (
                  <option value="">
                    No scopes, please contact your super.
                  </option>
                )}
              </select>
            </div>
          </div>
          {/* Work Description */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Work Description
            </label>{" "}
            <textarea
              value={shiftData.workDescription}
              onChange={(e) =>
                handleFieldChange("workDescription", e.target.value)
              }
              placeholder="Describe your work activities here..."
              rows={5}
              className="block caret-black w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              disabled={isLoading}
            ></textarea>
          </div>
          {/* Time In */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time In
            </label>{" "}
            <input
              type="time"
              value={shiftData.startTime || ""}
              onChange={(e) => handleFieldChange("startTime", e.target.value)}
              className="block w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>{" "}
          {/* Clock In Button */}
          <Button
            onClick={handleClockIn}
            variant={isLoading ? "disabled" : "secondary"}
            text={isLoading ? "" : "Clock In"}
            icon={isLoading ? "spinner" : null}
            className="py-3 px-6 w-full"
          />
        </div>
      </div>
    </div>
  );
};

export default ClockIn;
